import { logger } from '@tunasong/models'
import type { ClientToolContext } from './client-tools/context.js'
import { getClientToolImpl } from './client-tools/registry.js'

const RATE_LIMIT_PER_TURN = 8

export interface ClientToolCall {
  toolName: string
  toolCallId: string
  input: unknown
}

export type ClientToolDispatchResult =
  | { ok: true; result?: unknown }
  | { ok: false; error: string; limit?: number }

export interface ClientToolDispatcher {
  (call: ClientToolCall): Promise<ClientToolDispatchResult>
  reset(): void
}

/**
 * Build a per-Chat-instance dispatcher. The `getContext` callback fetches
 * the live editor / audio context at call time (React contexts can re-render
 * between calls). The rate-limit counter is closed over here; the controller
 * resets it on every new user turn via the returned `reset` method.
 *
 * When the registered executor throws, the rejection is caught and translated
 * into `{ ok: false, error: 'client_tool_failed: ...' }` so the Vercel AI SDK
 * sees a normal tool result and the loop continues; the original error is
 * logged for CloudWatch / browser console.
 */
export const createClientToolDispatcher = (
  getContext: () => ClientToolContext
): ClientToolDispatcher => {
  let callsThisTurn = 0
  const dispatch = async (call: ClientToolCall): Promise<ClientToolDispatchResult> => {
    if (callsThisTurn >= RATE_LIMIT_PER_TURN) {
      logger.warn('plugin-agent: client-tool rate limit hit', {
        toolName: call.toolName,
        limit: RATE_LIMIT_PER_TURN,
      })
      return { ok: false, error: 'rate_limited', limit: RATE_LIMIT_PER_TURN }
    }
    const impl = getClientToolImpl(call.toolName)
    if (!impl) {
      logger.warn('plugin-agent: unknown client tool', call.toolName)
      return { ok: false, error: 'unknown_client_tool' }
    }
    callsThisTurn += 1
    const ctx = { ...getContext(), callsThisTurn }
    try {
      return await impl(call.input, ctx)
    } catch (error) {
      logger.error('plugin-agent: client tool threw', { toolName: call.toolName, error })
      const message = error instanceof Error ? error.message : String(error)
      return { ok: false, error: `client_tool_failed: ${message}` }
    }
  }
  // Attach `reset` as a method on the dispatch function. The dispatcher is
  // exported as a function-with-method so callers can both call it and call
  // `.reset()` without juggling two references.
  const dispatcher = dispatch as ClientToolDispatcher
  dispatcher.reset = () => {
    callsThisTurn = 0
  }
  return dispatcher
}
